import { apiClient } from '../core/api';

export interface CustomisationSettings {
  /** PRD #18 Quick Reply Chips, as a JSON array string — see the `quickReplyChips` case below. */
  quickReplyChips?: string;
  /** PRD #18 Custom launcher: absolute URL of the merchant's own chat-button image. */
  launcherImage?: string;
  /** CS 2.0 需求 7: the FAQ questions listed under the welcome message, as a JSON array string (0–5). */
  faqQuestions?: string;
  /** CS 2.0 需求 7 (PRD V0.4): question → {answer, edited}, as a JSON object string. */
  faqAnswers?: string;
  /**
   * CS 2.0 需求 7 / 技术方案 11.1, READ-ONLY: the FAQ questions chatbot-api added from what shoppers
   * asked (JSON array string) — the settings page tags them "Auto". Never sent back: see
   * {@link SERVER_OWNED_KEYS}.
   */
  faqQuestionsAuto?: string;
  /**
   * CS 2.0 需求 7, READ-ONLY: `{"on_sale": bool, "discounts": bool}` as a JSON string — whether those
   * two chips have anything to show. Computed per read (not a stored row), only for a store on the
   * engine and only once known. Never sent back.
   */
  chipsAvailability?: string;
  /** CS 2.0 需求 10: where the urgent-handoff email goes (empty → supportEmail → account email). */
  urgentNotifyEmail?: string;
  /** CS 2.0 需求 10: the shopper's confirmation email for an urgent handoff (empty → default). */
  /** CS 2.0 需求 10: "true" / "false" — whether the shopper gets the confirmation email (on by default). */
  urgentConfirmationEnabled?: string;
  urgentConfirmationSubject?: string;
  urgentConfirmationBody?: string;
  agentName: string;
  avatarName: string;
  welcomeMessage: string;
  responseLength: string;
  widgetPosition: 'left' | 'right';
  widgetHandling: 'open' | 'closed';
  primaryColor: string;
  /** 0..1 — see components/.../widgetGlass. Stored as a string like every customisation. */
  glassLevel: string;
  companyLogo: string;
  enableLead: string;
  leadEmailNotification: string;
  leadEmailRecipient: string;
  leadGenerationSensitivity: string;
  leadOnStart: string;
  meetingLink: string;
  referrerUrl: string;
  subheading: string;
  supportEmail: string;
  widgetButton: string;
  widgetStatus: string;
  paymentStatus: string;
}

export interface CustomisationItem {
  key: string;
  value: string;
  type: 'chatbot' | 'digitalHuman';
  subCategory?: string;
  id?: string;
  userId: string;
  createdAt?: string;
  updatedAt?: string;
}

export type CustomisationApiResponse = CustomisationItem[];

/**
 * Keys the settings read returns but only the server writes. A save must never carry them:
 * `chipsAvailability` has no `default_customisations` row, so the backend would reject the WHOLE
 * save ("Key not valid"); the FAQ pair would be dropped server-side anyway, but a stale copy sent
 * back is exactly what that rule exists to stop.
 */
export const SERVER_OWNED_KEYS: ReadonlySet<string> = new Set([
  'chipsAvailability',
  'faqQuestionsAuto',
  'faqQuestionsDismissed',
]);

export class CustomisationService {
  // Store original API data to get IDs for updates
  private static originalApiData: CustomisationItem[] = [];

  /**
   * Get customisation settings for a user
   * Equivalent to Angular: GET /customisations/{userId}
   */
  static async getCustomisations(userId: string): Promise<{ settings: CustomisationSettings; rawData?: CustomisationApiResponse }> {
    const response = await apiClient.get<CustomisationApiResponse>(`/customisations/${userId}`);

    // Store original API data for later use in updates
    this.originalApiData = response.data;

    // Convert array response to settings object
    const settings = this.mapApiResponseToSettings(response.data);

    return { settings, rawData: response.data };
  }

  /**
   * Convert API array response to CustomisationSettings object
   */
  private static mapApiResponseToSettings(apiData: CustomisationItem[]): CustomisationSettings {
    const chatbotSettings: Partial<CustomisationSettings> = {};

    // Ensure apiData is an array
    if (!Array.isArray(apiData)) {
      console.warn('API response is not an array:', apiData);
      return this.getDefaultSettings();
    }

    // Filter for chatbot type settings and map to object
    apiData
      .filter(item => item.type === 'chatbot')
      .forEach(item => {
        switch (item.key) {
          case 'agentName':
            chatbotSettings.agentName = item.value;
            break;
          case 'avatarName':
            chatbotSettings.avatarName = item.value;
            break;
          case 'welcomeMessage':
            chatbotSettings.welcomeMessage = item.value;
            break;
          case 'responseLength':
            chatbotSettings.responseLength = item.value;
            break;
          case 'widgetPosition':
            chatbotSettings.widgetPosition = item.value.toLowerCase() as 'left' | 'right';
            break;
          case 'widgetStatus':
            chatbotSettings.widgetHandling = item.value as 'open' | 'closed';
            break;
          case 'widgetButton':
            chatbotSettings.widgetButton = item.value;
            break;
          case 'glassLevel':
            chatbotSettings.glassLevel = item.value;
            break;
          case 'colour':
            chatbotSettings.primaryColor = item.value;
            break;
          // PRD #18's Quick Reply Chips. A JSON array string, kept as the raw string all the way
          // through this layer: parsing belongs where it is rendered, and a corrupt value must not
          // be able to break loading every OTHER setting.
          case 'quickReplyChips':
            chatbotSettings.quickReplyChips = item.value;
            break;
          case 'launcherImage':
            chatbotSettings.launcherImage = item.value;
            break;
          case 'faqQuestions':
          case 'faqAnswers':
          case 'faqQuestionsAuto':
          case 'chipsAvailability':
          case 'urgentNotifyEmail':
          case 'urgentConfirmationEnabled':
          case 'urgentConfirmationSubject':
          case 'urgentConfirmationBody':
            (chatbotSettings as Record<string, string>)[item.key] = item.value;
            break;
          case 'companyLogo':
            chatbotSettings.companyLogo = item.value;
            break;
          case 'enableLead':
            chatbotSettings.enableLead = item.value;
            break;
          case 'leadEmailNotification':
            chatbotSettings.leadEmailNotification = item.value;
            break;
          case 'leadEmailRecipient':
            chatbotSettings.leadEmailRecipient = item.value;
            break;
          case 'leadGenerationSensitivity':
            chatbotSettings.leadGenerationSensitivity = item.value;
            break;
          case 'leadOnStart':
            chatbotSettings.leadOnStart = item.value;
            break;
          case 'meetingLink':
            chatbotSettings.meetingLink = item.value;
            break;
          case 'referrerUrl':
            chatbotSettings.referrerUrl = item.value;
            break;
          case 'subheading':
            chatbotSettings.subheading = item.value;
            break;
          case 'supportEmail':
            chatbotSettings.supportEmail = item.value;
            break;
          case 'paymentStatus':
            chatbotSettings.paymentStatus = item.value;
            break;
        }
      });

    // Set default values for missing fields
    return {
      // Optional keys pass through as read. quickReplyChips and launcherImage were parsed above and
      // then dropped here, so a saved chip arrangement (and a custom launcher) never came back on the
      // next load — the Settings Hub showed the defaults again every time.
      quickReplyChips: chatbotSettings.quickReplyChips,
      launcherImage: chatbotSettings.launcherImage,
      faqQuestions: chatbotSettings.faqQuestions,
      faqAnswers: chatbotSettings.faqAnswers,
      faqQuestionsAuto: chatbotSettings.faqQuestionsAuto,
      chipsAvailability: chatbotSettings.chipsAvailability,
      urgentNotifyEmail: chatbotSettings.urgentNotifyEmail,
      urgentConfirmationEnabled: chatbotSettings.urgentConfirmationEnabled,
      urgentConfirmationSubject: chatbotSettings.urgentConfirmationSubject,
      urgentConfirmationBody: chatbotSettings.urgentConfirmationBody,
      agentName: chatbotSettings.agentName || '',
      avatarName: chatbotSettings.avatarName || '',
      welcomeMessage: chatbotSettings.welcomeMessage || '',
      responseLength: chatbotSettings.responseLength || '100',
      widgetPosition: chatbotSettings.widgetPosition || 'right',
      widgetHandling: chatbotSettings.widgetHandling || 'closed',
      primaryColor: chatbotSettings.primaryColor || '#FFE5D9',
      glassLevel: chatbotSettings.glassLevel || '0.5',
      companyLogo: chatbotSettings.companyLogo || '',
      enableLead: chatbotSettings.enableLead || 'active',
      leadEmailNotification: chatbotSettings.leadEmailNotification || 'no',
      leadEmailRecipient: chatbotSettings.leadEmailRecipient || '',
      leadGenerationSensitivity: chatbotSettings.leadGenerationSensitivity || '0.3',
      leadOnStart: chatbotSettings.leadOnStart || 'yes',
      meetingLink: chatbotSettings.meetingLink || '',
      referrerUrl: chatbotSettings.referrerUrl || '',
      subheading: chatbotSettings.subheading || '',
      supportEmail: chatbotSettings.supportEmail || '',
      widgetButton: chatbotSettings.widgetButton || '{"launcher":"avatar","icon":"avatar","size":"small","label":"Chat with me"}',
      widgetStatus: chatbotSettings.widgetStatus || 'closed',
      paymentStatus: chatbotSettings.paymentStatus || 'startup',
    };
  }

  /**
   * Update customisation settings for a user
   * Equivalent to Angular: PUT /customisations/{userId}
   */
  static async updateCustomisations(
    userId: string,
    settings: Partial<CustomisationSettings>
  ): Promise<{ settings: CustomisationSettings }> {
    // Transform settings object to array format expected by API
    const payload = this.mapSettingsToApiPayload(settings);

    console.log('Sending customisation update payload:', payload);

    // Send PUT request
    const putResponse = await apiClient.put<CustomisationApiResponse & {
      errorCode?: number;
      errorMessage?: string;
    }>(`/customisations/${userId}`, payload);

    // apiClient rewrites every non-401/502 backend error into a 200 carrying {errorCode,
    // errorMessage}, so this `await` resolves on failure and nothing here would throw. Left
    // unchecked, a rejected save reported success and the merchant's change vanished on the next
    // load — the backend rejects the WHOLE request when one key is unknown, so they also lost
    // every other field they changed in the same save.
    const failure = putResponse.data as { errorCode?: number; errorMessage?: string } | undefined;
    if (failure?.errorCode) {
      throw new Error(failure.errorMessage ?? 'Could not save these settings.');
    }

    console.log('PUT request completed, fetching updated data...');

    // After PUT, fetch the latest data with GET request
    const getResponse = await this.getCustomisations(userId);
    return getResponse;
  }

  /**
   * Transform settings object to API payload array format (with IDs from original data)
   */
  private static mapSettingsToApiPayload(
    settings: Partial<CustomisationSettings>
  ): CustomisationItem[] {
    const payload: CustomisationItem[] = [];

    // Map each setting key-value pair to the API format
    Object.entries(settings).forEach(([key, value]) => {
      if (SERVER_OWNED_KEYS.has(key)) return;
      if (value !== undefined && value !== null) {
        // Convert React setting key to API key format
        let apiKey = key;
        if (key === 'primaryColor') {
          apiKey = 'colour'; // Map to API expected key
        } else if (key === 'widgetHandling') {
          apiKey = 'widgetStatus'; // Map to API expected key
        }

        // Find the original item to get the ID
        const originalItem = this.originalApiData.find(
          item => item.key === apiKey && item.type === 'chatbot'
        );

        if (originalItem) {
          payload.push({
            id: originalItem.id!,
            key: apiKey,
            value: String(value),
            type: 'chatbot',
            userId: originalItem.userId,
            createdAt: originalItem.createdAt,
            updatedAt: originalItem.updatedAt,
          });
        } else {
          // If no original item found, create new one without ID
          payload.push({
            key: apiKey,
            value: String(value),
            type: 'chatbot',
            userId: '', // Will be set by API
          });
        }
      }
    });

    return payload;
  }

  /**
   * Get default settings
   */
  private static getDefaultSettings(): CustomisationSettings {
    return {
      agentName: 'YepAI',
      avatarName: 'Bella',
      welcomeMessage: 'Hey there, how can I help you?',
      responseLength: 'short',
      widgetPosition: 'right',
      widgetHandling: 'closed',
      primaryColor: '#FFE5D9',
      glassLevel: '0.5',
      companyLogo: '',
      enableLead: 'active',
      leadEmailNotification: 'no',
      leadEmailRecipient: '',
      leadGenerationSensitivity: '0.3',
      leadOnStart: 'yes',
      meetingLink: '',
      referrerUrl: '',
      subheading: 'Our bot answers instantly',
      supportEmail: '',
      widgetButton: '{"launcher":"avatar","icon":"avatar","size":"small","label":"Chat with me"}',
      widgetStatus: 'closed',
      paymentStatus: 'startup',
    };
  }

  /**
   * Get user custom avatars
   * Calls GET /user-avatars/{userId}
   */
  static async getUserAvatars(userId: string): Promise<{ avatarUrl: string }[]> {
    const response = await apiClient.get<{ avatars: { avatarUrl: string }[]; totalCount: number }>(`/user-avatars/${userId}`);
    return response.data.avatars;
  }

  /**
   * Batch update customisation settings
   * Equivalent to Angular CustomisationUpdateService: PUT /customisations/{userId}/batch
   */
  static async batchUpdateCustomisations(
    userId: string,
    updates: Array<{ field: keyof CustomisationSettings; value: string }>
  ): Promise<{ settings: CustomisationSettings }> {
    const response = await apiClient.put<CustomisationApiResponse>(
      `/customisations/${userId}/batch`,
      { updates }
    );

    const mappedSettings = this.mapApiResponseToSettings(response.data);
    return { settings: mappedSettings };
  }

  /**
   * Reset customisation settings to default
   * Equivalent to Angular: POST /customisations/{userId}/reset
   */
  static async resetCustomisations(userId: string): Promise<{ settings: CustomisationSettings }> {
    const response = await apiClient.post<CustomisationApiResponse>(
      `/customisations/${userId}/reset`
    );

    const mappedSettings = this.mapApiResponseToSettings(response.data);
    return { settings: mappedSettings };
  }

  /**
   * Get customisation settings with query parameters
   */
  static async getCustomisationsWithParams(userId: string, params?: Record<string, string>): Promise<CustomisationApiResponse> {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    const response = await apiClient.get<CustomisationApiResponse>(`/customisations/${userId}${queryString}`);
    
    // Store original API data for later use in updates
    if (params?.subCategory) {
      // Merge with existing data instead of replacing
      this.originalApiData = [
        ...this.originalApiData.filter(item => item.subCategory !== params.subCategory),
        ...response.data
      ];
    }
    
    return response.data;
  }

  /**
   * Update customisations with raw payload (for abandoned cart settings)
   */
  static async updateCustomisationsRaw(userId: string, payload: CustomisationItem[]): Promise<void> {
    // Find existing items and update with their IDs
    const updatedPayload = payload.map(item => {
      const originalItem = this.originalApiData.find(
        orig => orig.key === item.key && orig.type === item.type && 
               (item.subCategory ? orig.subCategory === item.subCategory : true)
      );
      
      if (originalItem) {
        return {
          ...item,
          id: originalItem.id,
          createdAt: originalItem.createdAt,
          updatedAt: originalItem.updatedAt,
        };
      }
      
      return item;
    });

    await apiClient.put<CustomisationApiResponse>(`/customisations/${userId}`, updatedPayload);
  }
}
